import {
  getOperationsControllerGetOperationStationsQueryKey,
  getOperationsControllerGetOperationStationsQueryOptions,
  operationsControllerCreateOperation,
  operationsControllerGetOperationDetail,
  operationsControllerListBillsOfOperationsForOperation,
  operationsControllerListOperationCategories,
  operationsControllerListOperations,
  operationsControllerListOperationSubCategories,
  operationsControllerListRulesForOperation,
  operationsControllerReorderOperations,
  operationsControllerUpdateOperation,
  useOperationsControllerSetOperationStationEnabled,
  type OperationStationDto,
  type OperationUpdateDto,
} from '@artic/api-client';
import { toast } from '@artic/ui/toast';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import type {
  BillOfOperationsUsage,
  OperationCategory,
  OperationCreateInput,
  OperationDetail,
  OperationListItem,
  OperationRelevanceRule,
  OperationSubCategory,
  OperationUpdateInput,
} from './types';

export const OPERATIONS_KEY = 'operations' as const;

// ── List ──────────────────────────────────────────────────────────────

export function operationsListQueryOptions(
  partnerId: string | undefined,
  filters: { q?: string; show_archived?: boolean } = {},
) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'list', partnerId, filters] as const,
    queryFn: async ({ signal }): Promise<OperationListItem[]> => {
      const res = await operationsControllerListOperations(
        {
          partner_id: partnerId ?? '',
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.show_archived ? { show_archived: true } : {}),
        },
        { signal },
      );
      return expectData<OperationListItem[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ── Detail ────────────────────────────────────────────────────────────

export function operationDetailQueryOptions(operationId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'detail', operationId] as const,
    queryFn: async ({ signal }): Promise<OperationDetail> => {
      const res = await operationsControllerGetOperationDetail(operationId ?? '', { signal });
      return expectData<OperationDetail>(res);
    },
    enabled: !!operationId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ── Bills of Operations using the operation ──────────────────────────

export function operationBillsOfOperationsQueryOptions(operationId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'bills-of-operations', operationId] as const,
    queryFn: async ({ signal }): Promise<BillOfOperationsUsage[]> => {
      const res = await operationsControllerListBillsOfOperationsForOperation(operationId ?? '', {
        signal,
      });
      return expectData<BillOfOperationsUsage[]>(res);
    },
    enabled: !!operationId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ── Categories ────────────────────────────────────────────────────────

export function operationCategoriesQueryOptions() {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'categories'] as const,
    queryFn: async ({ signal }): Promise<OperationCategory[]> => {
      const res = await operationsControllerListOperationCategories({ signal });
      return expectData<OperationCategory[]>(res);
    },
    // Categories rarely change; cache for the whole session.
    staleTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// ── Sub-categories ────────────────────────────────────────────────────

/**
 * Sub-categories under a chosen category. The form's cascading
 * sub-category dropdown calls this when the category changes. When
 * `category` is null/undefined, the backend returns the full list.
 */
export function operationSubCategoriesQueryOptions(category: string | null | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'sub-categories', category ?? null] as const,
    queryFn: async ({ signal }): Promise<OperationSubCategory[]> => {
      const res = await operationsControllerListOperationSubCategories(
        category ? { operation_category: category } : undefined,
        { signal },
      );
      return expectData<OperationSubCategory[]>(res);
    },
    staleTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// ── Rules targeting this operation ───────────────────────────────────

/**
 * Relevance rules whose `operation_id` matches. Powers the "Rules"
 * panel on the operation detail page.
 */
export function operationRulesQueryOptions(operationId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'rules', operationId] as const,
    queryFn: async ({ signal }): Promise<OperationRelevanceRule[]> => {
      const res = await operationsControllerListRulesForOperation(operationId ?? '', { signal });
      return expectData<OperationRelevanceRule[]>(res);
    },
    enabled: !!operationId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ── Create ────────────────────────────────────────────────────────────

export function useCreateOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: OperationCreateInput) => {
      const res = await operationsControllerCreateOperation(input);
      return expectData<{ operation_id: string }>(res);
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'list', input.partner_id] });
    },
  });
}

// ── Reorder (admin only) ─────────────────────────────────────────────

interface ReorderOperationsArgs {
  partnerId: string;
  operationIds: string[];
}

/**
 * Admin-only: reassigns `operation_sequence` across the partner's
 * operations to match the supplied ordered array. Optimistically
 * rewrites the cached list with the new sequence values then rolls
 * back on error. Invalidates `bill_of_operations` because the matrix
 * sorts ops by `operation_sequence` too.
 */
export function useReorderOperations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ partnerId, operationIds }: ReorderOperationsArgs) => {
      await operationsControllerReorderOperations({
        partner_id: partnerId,
        operation_ids: operationIds,
      });
    },
    onMutate: async ({ partnerId, operationIds }) => {
      // Cancel any in-flight list refetches for this partner so they
      // don't overwrite the optimistic state.
      await queryClient.cancelQueries({
        queryKey: [OPERATIONS_KEY, 'list', partnerId],
      });
      // Snapshot every cached list query for this partner (search/archive
      // variants live under the same prefix) so onError can restore each.
      const snapshots = queryClient.getQueriesData<OperationListItem[]>({
        queryKey: [OPERATIONS_KEY, 'list', partnerId],
      });
      const orderMap = new Map(operationIds.map((id, i) => [id, i + 1]));
      for (const [key, data] of snapshots) {
        if (!data) continue;
        const updated = data
          .map((row) =>
            orderMap.has(row.operation_id)
              ? {
                  ...row,
                  operation_sequence: orderMap.get(row.operation_id) ?? row.operation_sequence,
                }
              : row,
          )
          .slice()
          .sort((a, b) => a.operation_sequence - b.operation_sequence);
        queryClient.setQueryData(key, updated);
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: (_data, _err, { partnerId }) => {
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'list', partnerId] });
      // Matrix sorts within each category by operation_sequence too.
      void queryClient.invalidateQueries({ queryKey: ['bill_of_operations'] });
    },
  });
}

// ── Update ────────────────────────────────────────────────────────────

interface UpdateOperationArgs {
  operationId: string;
  partnerId: string | undefined;
  input: OperationUpdateInput;
}

export function useUpdateOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ operationId, input }: UpdateOperationArgs) => {
      // Backend always cascades the update to Bill of Operations versions
      // via the `update_bills_of_operations: true` event flag — no client
      // option needed.
      //
      // `OperationUpdateInput` is the quality-side shape; it differs from the
      // generated `OperationUpdateDto` only in the structural typing of the
      // nested check items (the wire payload is identical). Cast to the DTO
      // the generated function expects.
      await operationsControllerUpdateOperation(operationId, input as OperationUpdateDto);
    },
    onSuccess: (_data, { operationId, partnerId }) => {
      // Invalidate detail (new version), list (version + counts), and
      // BoO usage (new BoO versions cascaded by the event handler).
      // Also invalidate the BoO matrix for the partner since the cascade
      // bumps Bills of Operations referenced by this operation.
      void queryClient.invalidateQueries({
        queryKey: [OPERATIONS_KEY, 'detail', operationId],
      });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'list', partnerId] });
      void queryClient.invalidateQueries({
        queryKey: [OPERATIONS_KEY, 'bills-of-operations', operationId],
      });
      void queryClient.invalidateQueries({ queryKey: ['bill_of_operations'] });
      // Product detail pages keyed by ['products', 'bill-of-operations', productId]
      // also surface the operation_version + applied_work_time / applied_headcount
      // for this operation. Invalidate the prefix so each product BoO query
      // re-fetches on next mount.
      void queryClient.invalidateQueries({ queryKey: ['products', 'bill-of-operations'] });
    },
  });
}

// ── Stations the operation is enabled on ("Operation Enabled On") ────────

/**
 * Partner stations + per-station enablement for this operation. Wraps the
 * generated Orval query options to apply the quality data rules (see
 * packages/quality/CLAUDE.md). The generated query function returns the
 * `{ data, status, headers }` envelope, so consumers read the list via
 * `query.data?.data`.
 */
export function operationStationsQueryOptions(operationId: string | undefined) {
  return getOperationsControllerGetOperationStationsQueryOptions(operationId ?? '', {
    query: {
      enabled: !!operationId,
      staleTime: 15_000,
      placeholderData: keepPreviousData,
    },
  });
}

/** The cached `{ data, status, headers }` envelope for the stations query. */
type OperationStationsEnvelope = { data: OperationStationDto[]; status: number };

/**
 * Toggles the operation on a single station. Optimistically flips the matching
 * station's `is_enabled` in the cached list (matched on the natural key the
 * server uses — factory/zone/station, NOT the surrogate station_id), rolls back
 * on error, and invalidates the stations query on settle. Station enablement is
 * decoupled from BoO change control, so nothing else is invalidated.
 */
export function useToggleOperationStation(operationId: string) {
  const queryClient = useQueryClient();
  const stationsKey = getOperationsControllerGetOperationStationsQueryKey(operationId);

  // Generics: the thrown error is an ApiError (extends Error); the optimistic
  // context carries the pre-mutation snapshot for rollback.
  return useOperationsControllerSetOperationStationEnabled<
    Error,
    { previous: OperationStationsEnvelope | undefined }
  >({
    mutation: {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: stationsKey });
        const previous = queryClient.getQueryData<OperationStationsEnvelope>(stationsKey);
        queryClient.setQueryData<OperationStationsEnvelope>(stationsKey, (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((station) =>
              station.factory_name === variables.data.factory_name &&
              station.zone_name === variables.data.zone_name &&
              station.station_name === variables.data.station_name
                ? { ...station, is_enabled: variables.data.is_enabled }
                : station,
            ),
          };
        });
        return { previous };
      },
      onError: (error, _variables, context) => {
        if (context?.previous !== undefined) {
          queryClient.setQueryData(stationsKey, context.previous);
        }
        toast.error(error instanceof Error ? error.message : 'Failed to update station enablement');
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: stationsKey });
      },
    },
  });
}
